import * as FRAGS from '@thatopen/fragments';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { createContext, memo, useCallback, useContext, useDeferredValue, useEffect, useMemo, useState, type FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

import {
  applyToggle,
  buildCheckMap,
  CHECK_NONE,
  collectIds,
  flattenUnnamed,
  idMatchBadge,
  itemName,
  loadSpatialTree,
  nodeMatchesQuery,
  nodeTooltip,
  type CheckMap,
  type CheckState,
  type ItemInfoMap,
} from './fragmentTreeData';
import { getFragments } from './IFC2';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';

interface Props {
  /** Layer id — the fragment model is `frag-${layerId}`. */
  layerId: string;
  /** Checked localIds (leaf + container ids, i.e. the union of checked subtrees). */
  selected: ReadonlySet<number>;
  /**
   * Functional updater (React `setState` compatible). Toggles are applied
   * against the *latest* selection, so rapid clicks can't clobber each other.
   */
  onChange: (update: (prev: Set<number>) => Set<number>) => void;
  /** Freeze the tree (e.g. while the export mutation is in flight). */
  disabled?: boolean;
}

/**
 * Search state, precomputed once per (tree, items, query) so each row does
 * O(1) `has` lookups instead of re-walking its subtree on every render.
 */
interface SearchState {
  query: string;
  /** Nodes whose own name, category or identifier matches `query`. */
  selfMatches: WeakSet<FRAGS.SpatialTreeItem>;
  /** Nodes themselves OR with a matching descendant (= "in scope"). */
  inScope: WeakSet<FRAGS.SpatialTreeItem>;
}

const EMPTY_SEARCH_STATE: SearchState = {
  query: '',
  selfMatches: new WeakSet(),
  inScope: new WeakSet(),
};

const ItemsContext = createContext<ItemInfoMap>(new Map());
const SearchContext = createContext<SearchState>(EMPTY_SEARCH_STATE);
/** Nodes to expand on first render so a pre-selection is visible. */
const AutoOpenContext = createContext<WeakSet<FRAGS.SpatialTreeItem>>(new WeakSet());

/** Single post-order walk filling both selfMatches and inScope. */
function buildSearchState(root: FRAGS.SpatialTreeItem | null, items: ItemInfoMap, q: string): SearchState {
  if (!q || !root) return EMPTY_SEARCH_STATE;
  const selfMatches = new WeakSet<FRAGS.SpatialTreeItem>();
  const inScope = new WeakSet<FRAGS.SpatialTreeItem>();
  const visit = (node: FRAGS.SpatialTreeItem): boolean => {
    let any = nodeMatchesQuery(node, items, q);
    if (any) selfMatches.add(node);
    if (node.children) {
      for (const c of node.children) {
        if (visit(c)) any = true;
      }
    }
    if (any) inScope.add(node);
    return any;
  };
  visit(root);
  return { query: q, selfMatches, inScope };
}

interface RowProps {
  node: FRAGS.SpatialTreeItem;
  depth: number;
  /** This node's tri-state — handed down by the parent so `memo` can compare it. */
  state: CheckState;
  /**
   * The whole tri-state map, passed as a plain prop (NOT read through a ref or
   * a stable accessor): the app is built with the React Compiler, which
   * memoises `getCheck(child)` on its inputs and would never see a new map
   * behind a stable function. `areRowPropsEqual` ignores this prop on purpose —
   * a row only needs the latest map when it re-renders, and `buildCheckMap`'s
   * structural sharing guarantees it re-renders whenever any descendant changed.
   */
  checkMap: CheckMap;
  onToggle: (ids: number[], on: boolean) => void;
  disabled: boolean;
}

const areRowPropsEqual = (a: RowProps, b: RowProps) =>
  a.node === b.node && a.depth === b.depth && a.state === b.state && a.onToggle === b.onToggle && a.disabled === b.disabled;

// NOTE: the inner function is named `TreeRowImpl`; recursive children below
// render `TreeRow` (the memoized wrapper), never the raw implementation.
const TreeRow = memo(function TreeRowImpl({ node, depth, state, checkMap, onToggle, disabled }: RowProps) {
  const items = useContext(ItemsContext);
  const search = useContext(SearchContext);
  const autoOpen = useContext(AutoOpenContext);
  const query = search.query;

  // Collapsed below depth 1 — a whole IFC model expanded at once is unusable
  // inside a dialog — except along the path to a pre-selection.
  const [open, setOpen] = useState(() => depth < 1 || autoOpen.has(node));

  const subtreeIds = useMemo(() => collectIds(node), [node]);
  const flat = useMemo(() => flattenUnnamed(node.children, items), [node.children, items]);
  const selfMatches = query ? search.selfMatches.has(node) : false;
  const displayChildren = useMemo(() => {
    if (!query || selfMatches) return flat;
    return flat.filter((c) => search.inScope.has(c));
  }, [flat, query, selfMatches, search]);

  const hasChildren = displayChildren.length > 0;
  const effectiveOpen = query ? true : open;
  const name = itemName(items, node.localId);
  const cat = node.category ?? undefined;
  const label = name ?? cat ?? '?';
  // Shown only when the row matched through an identifier rather than its
  // name/class, so the user can see why it's in the result list.
  const idBadge = query ? idMatchBadge(node, items, query) : undefined;
  const title = nodeTooltip(label, node, items);

  const toggleSelf = useCallback(() => onToggle(subtreeIds, !state.all), [onToggle, subtreeIds, state.all]);

  return (
    <div className='select-none'>
      <div className='flex items-center gap-1 px-1 py-0.5 rounded hover:bg-slate-100 dark:hover:bg-stone-800/60'>
        {hasChildren ? (
          <button
            type='button'
            onClick={() => setOpen((o) => !o)}
            className='size-4 shrink-0 flex items-center justify-center text-slate-400'
            title={label}
            aria-label={label}
            aria-expanded={effectiveOpen}>
            {effectiveOpen ? <ChevronDown className='size-3' /> : <ChevronRight className='size-3' />}
          </button>
        ) : (
          <span className='size-4 shrink-0' aria-hidden />
        )}
        <Checkbox
          checked={state.all ? true : state.some ? 'indeterminate' : false}
          indeterminate={state.some}
          disabled={disabled}
          // Radix reports `true` when leaving the indeterminate state; treat
          // anything that isn't an explicit `false` as "check the whole subtree".
          onCheckedChange={(v) => onToggle(subtreeIds, v !== false)}
          className='shrink-0 data-[state=indeterminate]:bg-primary data-[state=indeterminate]:border-primary data-[state=indeterminate]:text-primary-foreground'
        />
        <button
          type='button'
          onClick={toggleSelf}
          disabled={disabled}
          className='flex flex-1 min-w-0 items-center gap-2 text-left cursor-pointer disabled:cursor-default'
          title={title}>
          <span className={cn('text-xs flex-1 truncate font-mono', selfMatches && 'font-semibold')}>{label}</span>
          {idBadge && (
            <span className='ml-1 shrink-0 text-[10px] font-mono text-slate-400 truncate max-w-24'>{idBadge}</span>
          )}
        </button>
      </div>
      {effectiveOpen && hasChildren && (
        <div className='pl-3 border-l border-slate-200/70 dark:border-stone-700/40 ml-2'>
          {displayChildren.map((child, i) => (
            <TreeRow
              key={`${child.category ?? ''}-${child.localId}-${i}`}
              node={child}
              depth={depth + 1}
              state={checkMap.get(child) ?? CHECK_NONE}
              checkMap={checkMap}
              onToggle={onToggle}
              disabled={disabled}
            />
          ))}
        </div>
      )}
    </div>
  );
}, areRowPropsEqual);
TreeRow.displayName = 'TreeRow';

/**
 * Tri-state checkbox tree over one fragment model's spatial structure, used
 * by `LandxmlExportDialog` to scope an export to a subset of elements.
 * Container ids are included in the selection — `resolveGuids` drops the ones
 * without a `_guid`, and the exporter ignores products without geometry.
 */
const LandxmlExportTree: FunctionComponent<Props> = ({ layerId, selected, onChange, disabled = false }) => {
  const { t } = useTranslation('project');
  const [tree, setTree] = useState<FRAGS.SpatialTreeItem | null>(null);
  const [items, setItems] = useState<ItemInfoMap>(new Map());
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [query, setQuery] = useState('');
  // Deferred so typing never blocks on the (potentially large) tree filter.
  const deferredQuery = useDeferredValue(query.trim().toLowerCase());

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setUnavailable(false);
    setTree(null);
    setItems(new Map());
    (async () => {
      try {
        const fragments = await getFragments();
        const model = fragments.models.list.get(`frag-${layerId}`);
        if (!model) {
          if (!cancelled) {
            setUnavailable(true);
            setLoading(false);
          }
          return;
        }
        const data = await loadSpatialTree(model, {
          onTree: (structure) => {
            if (!cancelled) {
              setTree(structure);
              setLoading(false);
            }
          },
          isCancelled: () => cancelled,
        });
        if (cancelled) return;
        setItems(data.items);
      } catch {
        if (!cancelled) {
          setUnavailable(true);
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [layerId]);

  const search = useMemo(() => buildSearchState(tree, items, deferredQuery), [tree, items, deferredQuery]);

  // Tri-state for every node, recomputed once per tick (not once per row) and
  // structurally shared against the previous map so memoised rows can skip
  // untouched subtrees. The previous map lives in a state-held cache object
  // (not a ref read during render — see RowProps.checkMap for why that matters
  // under the React Compiler).
  const [checkCache] = useState<{ prev: CheckMap }>(() => ({ prev: new WeakMap() }));
  const checkMap = useMemo(() => {
    const next = buildCheckMap(tree, selected, checkCache.prev);
    checkCache.prev = next;
    return next;
  }, [tree, selected, checkCache]);

  // Expand down to whatever was pre-selected when the dialog opened (the
  // subtree picked in the drawer): partially-selected nodes are exactly the
  // ancestor chain — a fully-selected node reads as checked and stays closed.
  const [initialSelected] = useState<ReadonlySet<number>>(() => selected);
  const autoOpen = useMemo(() => {
    const set = new WeakSet<FRAGS.SpatialTreeItem>();
    if (!tree || initialSelected.size === 0) return set;
    const initialMap = buildCheckMap(tree, initialSelected, new WeakMap());
    const visit = (node: FRAGS.SpatialTreeItem) => {
      if (initialMap.get(node)?.some) set.add(node);
      node.children?.forEach(visit);
    };
    visit(tree);
    return set;
  }, [tree, initialSelected]);

  // Stable across ticks (as long as `onChange` is — React's setState is) so
  // `memo(TreeRow)` can skip subtrees whose tri-state didn't change. The
  // functional updater applies the toggle to the latest selection.
  const toggle = useCallback(
    (ids: number[], on: boolean) => onChange((prev) => applyToggle(prev, ids, on) ?? prev),
    [onChange],
  );

  // A bare grouping root (no category, no name) contributes nothing but a "?"
  // row, so its flattened children become the top level — same as the drawer.
  const roots = useMemo(() => {
    if (!tree) return [];
    const isUnnamedRoot = tree.category == null && itemName(items, tree.localId) === undefined;
    const list = isUnnamedRoot ? flattenUnnamed(tree.children, items) : [tree];
    if (!deferredQuery) return list;
    return list.filter((n) => search.inScope.has(n));
  }, [tree, items, deferredQuery, search]);

  if (unavailable) return <p className='text-xs text-muted-foreground'>{t('landxmlExport.treeUnavailable')}</p>;

  return (
    <div className='space-y-2'>
      <Input
        placeholder={t('landxmlExport.treeSearch')}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        disabled={disabled}
        className='h-8 text-xs'
      />
      <div className='border border-slate-200/70 rounded-md bg-slate-50/40 dark:bg-stone-900/40 dark:border-stone-700/40 max-h-64 overflow-y-auto px-2 py-1.5 text-xs font-mono'>
        {loading || !tree ? (
          <div className='flex items-center gap-2 text-xs text-muted-foreground px-1 py-1'>
            <Loader2 className='size-3 animate-spin' />
            {t('landxmlExport.treeLoading')}
          </div>
        ) : (
          <ItemsContext.Provider value={items}>
            <SearchContext.Provider value={search}>
              <AutoOpenContext.Provider value={autoOpen}>
                {roots.map((node, i) => (
                  <TreeRow
                    key={`${node.category ?? ''}-${node.localId}-${i}`}
                    node={node}
                    depth={0}
                    state={checkMap.get(node) ?? CHECK_NONE}
                    checkMap={checkMap}
                    onToggle={toggle}
                    disabled={disabled}
                  />
                ))}
              </AutoOpenContext.Provider>
            </SearchContext.Provider>
          </ItemsContext.Provider>
        )}
      </div>
    </div>
  );
};

export default LandxmlExportTree;
